import type { App, DirectiveBinding, ObjectDirective } from 'vue';

/**
 * `v-tooltip-on-clip` — shows a Vue Foundation tooltip on an element ONLY when
 * its content is actually being clipped: either truncated by
 * `text-overflow: ellipsis` (Tailwind `truncate`) or hidden by a fixed-height
 * `overflow: hidden` container.
 *
 * Usage:
 *   <div class="rc-booking" v-tooltip-on-clip>…several truncating lines…</div>
 *     → when the element OR any descendant line is clipped, the tooltip shows
 *       the element's full rendered text (`innerText`, line breaks preserved).
 *
 *   <span class="name" v-tooltip-on-clip="fullName">{{ fullName }}</span>
 *     → same, but show the provided string instead of the rendered text. Pass
 *       `null`/`false` to disable.
 *
 * It delegates rendering/positioning to the foundation `v-tooltip` directive
 * (so styling + behavior stay identical to every other tooltip), and
 * re-evaluates clip state on resize and on content changes via
 * ResizeObserver + MutationObserver.
 */

type TooltipValue = string | null | false | undefined;
type DelegateHook = (el: HTMLElement, binding: { value: TooltipValue }) => void;

interface ClipState {
    /** Explicit override; `undefined` → derive content from `innerText`. */
    text: TooltipValue;
    /** Last value handed to the tooltip directive, to skip redundant calls. */
    applied: TooltipValue;
    resize: ResizeObserver;
    mutation: MutationObserver;
}

const states = new WeakMap<HTMLElement, ClipState>();

/**
 * Clipped along an axis whose overflow is genuinely hidden/clipped — so a
 * `overflow: auto/scroll` element (a real scroller) is not mistaken for a
 * truncation. The size check short-circuits before the (cheap) style read.
 */
function isElClipped(el: HTMLElement): boolean {
    const overflowsX = el.scrollWidth - el.clientWidth > 1;
    const overflowsY = el.scrollHeight - el.clientHeight > 1;
    if (!overflowsX && !overflowsY) return false;
    const style = getComputedStyle(el);
    const xClipped = style.overflowX === 'hidden' || style.overflowX === 'clip';
    const yClipped = style.overflowY === 'hidden' || style.overflowY === 'clip';
    return (overflowsX && xClipped) || (overflowsY && yClipped);
}

/** Is the element — or any descendant (e.g. a truncating line) — clipped? */
function isClipped(el: HTMLElement): boolean {
    if (isElClipped(el)) return true;
    for (const child of el.querySelectorAll<HTMLElement>('*')) {
        if (isElClipped(child)) return true;
    }
    return false;
}

export function registerClipTooltipDirective(app: App) {
    // Reuse the foundation tooltip directive for rendering — keeps styling and
    // positioning identical to every other `v-tooltip`. It is registered by
    // installVf() (via setupVf), which runs before this in main.ts.
    const tooltip = app.directive('tooltip') as ObjectDirective<HTMLElement, TooltipValue> | undefined;
    const ttApply = tooltip?.mounted as unknown as DelegateHook | undefined;
    const ttDestroy = tooltip?.unmounted as unknown as DelegateHook | undefined;

    function setTooltip(el: HTMLElement, value: TooltipValue) {
        const state = states.get(el);
        if (!state || state.applied === value) return;
        state.applied = value;
        // The foundation tooltip directive treats null/false as "no tooltip" and
        // reconfigures on every call, so one invocation covers create/update/destroy.
        ttApply?.(el, { value });
    }

    function sync(el: HTMLElement) {
        const state = states.get(el);
        if (!state) return;
        // Explicitly disabled.
        if (state.text === null || state.text === false) return setTooltip(el, null);
        if (!isClipped(el)) return setTooltip(el, null);
        const content = typeof state.text === 'string' ? state.text : el.innerText.trim();
        setTooltip(el, content || null);
    }

    app.directive('tooltip-on-clip', {
        mounted(el: HTMLElement, binding: DirectiveBinding<TooltipValue>) {
            const resize = new ResizeObserver(() => sync(el));
            const mutation = new MutationObserver(() => sync(el));
            states.set(el, { text: binding.value, applied: undefined, resize, mutation });
            resize.observe(el);
            mutation.observe(el, { childList: true, subtree: true, characterData: true });
            // ResizeObserver fires an initial callback after layout; this rAF is a
            // belt-and-suspenders first measure in case layout isn't settled yet.
            requestAnimationFrame(() => sync(el));
        },
        updated(el: HTMLElement, binding: DirectiveBinding<TooltipValue>) {
            const state = states.get(el);
            if (state) state.text = binding.value;
            sync(el);
        },
        unmounted(el: HTMLElement) {
            const state = states.get(el);
            if (!state) return;
            state.resize.disconnect();
            state.mutation.disconnect();
            ttDestroy?.(el, { value: null });
            states.delete(el);
        }
    });
}
